'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const userPickerInputVariants = cva(
	'w-full border border-border text-text-primary focus:border-accent focus:outline-none',
	{
		variants: {
			size: {
				/** Form compatti in sidebar/pannello. */
				sm: 'rounded-md bg-card px-2.5 py-1.5 text-[13px]',
				/** Input di dimensione da form di dettaglio. */
				md: 'rounded-lg bg-background px-3 py-2 text-[13px]',
			},
		},
		defaultVariants: {
			size: 'sm',
		},
	},
);

export interface UserPickerExistingUser {
	/** Email dell'account esistente trovato. */
	email: string;
	/** Nome completo dell'account esistente, se impostato. */
	fullName: string | null;
}

export interface UserPickerFieldsProps {
	/** Dimensione degli input. Default: `sm`. */
	size?: VariantProps<typeof userPickerInputVariants>['size'];
	/** Valore corrente del campo email. */
	email: string;
	/** Chiamata a ogni digitazione nel campo email. */
	onEmailChange: (v: string) => void;
	/** Valore corrente del campo nome e cognome. */
	fullName: string;
	/** Chiamata a ogni digitazione nel campo nome e cognome. */
	onFullNameChange: (v: string) => void;
	/** Valore corrente del campo password. */
	password: string;
	/** Chiamata a ogni digitazione nel campo password. */
	onPasswordChange: (v: string) => void;
	/** Non null quando l'email digitata corrisponde a un account già esistente. */
	existing: UserPickerExistingUser | null;
	/** Se true, l'email digitata ha un formato valido. */
	emailValid: boolean;
	/** Se true, è in corso la verifica dell'email (lookup account esistente). */
	checking: boolean;
	/** Etichetta del ruolo mostrata nel messaggio di conferma per un utente esistente (es. "Referente"). */
	roleLabel: string;
}

/**
 * Campi per inserire un utente a partire dall'email. Mostra una riga di conferma quando
 * l'email corrisponde a un account già esistente, altrimenti rivela i campi nome + password.
 * La logica di ricerca/debounce dell'email è a carico del chiamante.
 */
const UserPickerFields = ({
	email,
	onEmailChange,
	fullName,
	onFullNameChange,
	password,
	onPasswordChange,
	existing,
	emailValid,
	checking,
	roleLabel,
	size,
}: UserPickerFieldsProps) => {
	const inputCls = userPickerInputVariants({ size });
	const showNewAccountFields = emailValid && !existing && !checking;

	return (
		<div className="space-y-2">
			<input
				type="email"
				placeholder="Email"
				value={email}
				onChange={(e) => onEmailChange(e.target.value)}
				className={inputCls}
			/>
			{emailValid && existing && (
				<p className="text-[12px] text-accent">
					Utente esistente: <strong>{existing.fullName ?? existing.email}</strong> - verrà aggiunto
					come {roleLabel}.
				</p>
			)}
			{showNewAccountFields && (
				<>
					<input
						placeholder="Nome e cognome"
						value={fullName}
						onChange={(e) => onFullNameChange(e.target.value)}
						className={inputCls}
					/>
					<input
						type="password"
						placeholder="Password (min 8 caratteri)"
						value={password}
						onChange={(e) => onPasswordChange(e.target.value)}
						className={inputCls}
					/>
				</>
			)}
		</div>
	);
};
UserPickerFields.displayName = 'UserPickerFields';

export { UserPickerFields, userPickerInputVariants };
